import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import isUndefined from 'lodash/isUndefined';
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import last from 'lodash/last';
import each from 'lodash/each';
import * as AuthHelper from '@Services/Auth/AuthHelper';
import Catalyst from '@Services/Catalyst';
import HttpRequest from '@Services/RequestFactory';
import RequestUrls from '@Constants/RequestUrlsConstants';
import SimpleTmpl from '@Services/SimpleTmpl';
import { imageTypes } from '@Constants/rx-ui/constants';
import { resultsConfig } from '@Constants/Results';
import { eachHierarchy } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { updateUrlWithStaticHost } from '@Utils/StaticHostUrl';

const { CONTEXT } = resultsConfig;

/**
 * @ngdoc function
 * @name _getZoomArrayFromPath
 * @param {Object} path
 * @returns {Array}
 * @description get sorted array for zooms
 * @private
 */
function _getZoomArrayFromPath(path) {
  const zoomMap = {
    [imageTypes.RESULTS_S]: 0,
    [imageTypes.PLANNER_XS]: 0,
    [imageTypes.RESULTS_M]: 1,
    [imageTypes.PLANNER_S]: 1,
    [imageTypes.RESULTS_L]: 2,
    [imageTypes.PLANNER_M]: 2,
    [imageTypes.HUGESUBST_MODAL]: undefined,
  };

  return sortBy(path, (imgObj) => {
    return zoomMap[imgObj.meta];
  });
}

/**
 * @ngdoc method
 * @method formatPath
 * @methodOf app.results:FormatUtils
 * @param path {string|array} - path to image without host prefix and access token
 * @description Adds configured host url and access token when needed to image path.
 * @returns {String|Array} depending on path type, result will be the same type. Updated image path.
 */
export function formatPath(path) {
  if (isUndefined(path)) {
    const errorMessage = 'Invalid path param passed';
    Catalyst.trackError({
      message: errorMessage,
      component: 'formatPath',
    });
    throw new Error(errorMessage);
  }

  const isPathObject = isObject(path);
  const zoomArray = _getZoomArrayFromPath(path);
  const rawPaths = isPathObject ? zoomArray.map((imgObj) => imgObj.url) : [path];
  const token = AuthHelper.getAccessToken();

  const resultPath = rawPaths.map((rawPath) => {
    const prefix = HttpRequest.getUrlWithPrefix('');

    if (!rawPath || rawPath.indexOf(prefix) === 0) {
      return rawPath;
    }

    const params = {
      imgPath: rawPath,
    };

    let template;
    if (!RequestUrls.noImage.urlRegex.test(rawPath)) {
      params.accessToken = token;
      template = RequestUrls.relativeResultsImage.url;
    } else {
      template = RequestUrls.relativePublicImage.url;
    }

    return SimpleTmpl.format(template, params, true);
  });

  return isPathObject ? resultPath : resultPath[0];
}

/**
 * @ngdoc function
 * @name _formatFacts
 * @param facts
 * @description adds class name to each fact by transforming its title (lower case, alphanumeric, connected by dash)
 * @private
 */
function _formatFacts(facts) {
  if (!facts || !facts.length) {
    return;
  }
  eachHierarchy(facts, (fact) => {
    // eslint-disable-next-line no-param-reassign
    fact.className = fact.title
      .toLowerCase()
      .replace(/[^-\w\s!?]/g, '')
      .replace(/ /g, '-');
  });
}

/**
 * @ngdoc function
 * @name _formatAvailabilityUrls
 * @param entity
 * @description adds BE host and access_token as param
 * @private
 */
function _formatAvailabilityUrls(entity) {
  if (!entity.availability) return;

  const { url, providers } = entity.availability;

  if (url) {
    // eslint-disable-next-line no-param-reassign
    entity.availability.url = formatPath(url);
  }

  if (!providers) {
    return;
  }

  providers.forEach((provider) => {
    if (provider.custom) {
      // eslint-disable-next-line no-param-reassign
      provider.imageUrl = updateUrlWithStaticHost(provider.imageUrl);
      return;
    }

    // eslint-disable-next-line no-param-reassign
    provider.imageUrl = formatPath(provider.imageUrl);
    // eslint-disable-next-line no-param-reassign
    provider.linkUrl = formatPath(provider.linkUrl);
  });
}

/**
 * @ngdoc method
 * @method _formatSubstances
 * @methodOf app.results:FormatUtils
 * @param {Array} substances json from BE
 * @description Formatting raw BE data for UI needs
 * @returns {Array} with formatted data
 * @private
 */
function _formatSubstances(substances) {
  each(substances, (substance) => {
    _formatFacts(substance.facts);
    _formatAvailabilityUrls(substance);

    const mixtureItems = get(substance, 'details.mixtureItems');

    if (mixtureItems) {
      mixtureItems.forEach((mixture) => _formatAvailabilityUrls(mixture));
    }
  });

  return substances;
}

function _getFormattedComponent(reaction, component) {
  let entities;
  if (reaction && isArray(reaction[component])) {
    entities = reaction[component];
    entities.forEach((entity) => _formatAvailabilityUrls(entity));
  }
  return entities;
}

/**
 * @ngdoc method
 * @method _formatReactions
 * @methodOf app.results:FormatUtils
 * @param {Array} reactions json from BE
 * @description Formatting raw BE data for UI needs
 * @returns {Array} with formatted data
 * @private
 */
function _formatReactions(reactions) {
  each(reactions, (reaction) => {
    const reactants = _getFormattedComponent(reaction, 'reactants');
    const products = _getFormattedComponent(reaction, 'products');
    const rxnPath = get(reaction, 'rxnPath.url');

    if (rxnPath) {
      // eslint-disable-next-line no-param-reassign
      reaction.rxnPath = formatPath(rxnPath);
    }

    if (isArray(reactants) && reactants.length > 0) {
      // eslint-disable-next-line no-param-reassign
      reaction.reactants = reactants;
    }

    if (isArray(products) && products.length > 0) {
      // eslint-disable-next-line no-param-reassign
      reaction.products = products;
    }
  });

  return reactions;
}

/**
 * @ngdoc method
 * @method _formatTargets
 * @methodOf app.results:FormatUtils
 * @param {Array} targets json from BE
 * @description Formatting raw BE data for UI needs
 * @returns {Array} with formatted data
 * @private
 */
function _formatTargets(targets) {
  each(targets, (target) => {
    const substance = target.mostActiveSubstanceDetails;
    if (substance && substance.substanceEntity) {
      _formatAvailabilityUrls(substance.substanceEntity);
    }
  });

  return targets;
}

const formatEntitiesProcessors = {
  [CONTEXT.substances]: _formatSubstances,
  [CONTEXT.reactions]: _formatReactions,
  [CONTEXT.targets]: _formatTargets,
  [CONTEXT.citations]: (data) => data,
};

function _processEntitiesByContext(context, entities, component) {
  const processor = formatEntitiesProcessors[context];

  if (isUndefined(processor)) {
    const errorMessage = `Not supported context passed: ${context}.`;
    Catalyst.trackError({
      message: errorMessage,
      component,
    });
    throw new Error(errorMessage);
  }

  return processor(entities);
}

/**
 * @ngdoc method
 * @method formatEntitiesByContext
 * @methodOf app.results:FormatUtils
 * @param {String} context
 * @param {Array|Object} entities
 * @description Formatting raw BE data for UI needs
 * @returns {Array} with formatted data
 */
export function formatEntitiesByContext(context, entities) {
  if (!(isArray(entities) || isObject(entities))) {
    return Logger.error(`Invalid entities param passed: ${entities}. Only array and object types are supported`);
  }
  return _processEntitiesByContext(context, entities, 'formatEntitiesByContext');
}

/**
 * @ngdoc method
 * @method formatEntityByContext
 * @methodOf app.results:FormatUtils
 * @param {String} context
 * @param {Object} entity
 * @description Formatting raw BE data for UI needs
 * @returns {Object} with formatted data
 */
export function formatEntityByContext(context, entity) {
  const result = _processEntitiesByContext(context, [entity], 'formatEntityByContext');
  return result[0];
}

/**
 * @ngdoc method
 * @name getImageFromPathForZoom
 * @methodOf app.results:FormatUtils
 * @param {Object} path
 * @param {Number} zoom
 * @param {Boolean} [formatPath]
 * @returns {Object}
 * @description Formatting raw BE data for UI needs
 */
export function getImageFromPathForZoom(path, zoom, isFormattedPath = false) {
  const zoomArr = _getZoomArrayFromPath(path);
  const imgObj = zoomArr[zoom] ? zoomArr[zoom] : last(zoomArr);

  if (isFormattedPath && imgObj.url) {
    // format image url if exist
    imgObj.url = formatPath(imgObj.url);
  }

  return imgObj;
}
